/*
 * Optional first-name personalization (Stage 6).
 * sessionStorage only — never sent to a server, cookie, or URL. Sanitized input
 * (letters/spaces/hyphens/apostrophes/periods; placeholders rejected; 40-char cap).
 * Output is always rendered as plain text. Adapted from the prototype personalize.js.
 */
import React, { useCallback, useEffect, useState } from "react";

const KEY = "myschoolrx_guestFirstName";
const MAX = 40;
const REJECT = /^(undefined|null|guest|\[name\]|n\/a|test)$/i;
const ALLOWED = /^[\p{L}\p{M}][\p{L}\p{M}'’ .-]*$/u;
const EVT = "msrx-guestname";

const normalize = (v) => String(v == null ? "" : v).replace(/\s+/g, " ").trim().slice(0, MAX).trim();
export const safeName = (v) => {
  const n = normalize(v);
  return n && !REJECT.test(n) && ALLOWED.test(n) ? n : "";
};
const store = () => {
  try { const s = window.sessionStorage; s.getItem(KEY); return s; } catch { return null; }
};
export const getGuestName = () => { const s = store(); return s ? safeName(s.getItem(KEY)) : ""; };
export const setGuestName = (v) => {
  const s = store();
  const n = safeName(v);
  if (!s) return "";
  try { if (n) s.setItem(KEY, n); else s.removeItem(KEY); } catch { /* ignore */ }
  return n;
};
export const clearGuestName = () => { const s = store(); if (s) { try { s.removeItem(KEY); } catch { /* ignore */ } } };
export const guestNameAvailable = () => !!store();

export function useGuestName() {
  const [name, setName] = useState(getGuestName());
  useEffect(() => {
    const h = () => setName(getGuestName());
    window.addEventListener(EVT, h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener(EVT, h); window.removeEventListener("storage", h); };
  }, []);
  const save = useCallback((v) => { const n = setGuestName(v); window.dispatchEvent(new Event(EVT)); return n; }, []);
  const clear = useCallback(() => { clearGuestName(); window.dispatchEvent(new Event(EVT)); }, []);
  return { name, save, clear };
}

/* Landing field — optional, no gating. */
export const GuestNameField = ({ className = "" }) => {
  const { name, save, clear } = useGuestName();
  const [draft, setDraft] = useState(name);
  const [msg, setMsg] = useState(name ? `Thanks, ${name}.` : "");
  const [err, setErr] = useState(false);
  if (!guestNameAvailable()) return null;

  const onChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    if (!v.trim()) { save(""); setErr(false); setMsg(""); return; }
    const n = safeName(v);
    if (n) { save(n); setErr(false); setMsg(`Thanks, ${n}.`); }
    else { setErr(true); setMsg("Please enter just a first name, or leave this blank."); }
  };
  const onClear = () => { clear(); setDraft(""); setErr(false); setMsg("Name cleared."); };

  return (
    <div className={`rounded-xl border border-[#e5e7eb] bg-white p-4 sm:p-5 ${className}`} data-testid="guest-name-field">
      <label htmlFor="guest-first-name" className="block text-[15px] font-semibold text-[#0f172a]">What should we call you?</label>
      <p id="guest-first-name-hint" className="mt-1 text-[13px] text-[#6b7280]">Optional, first name only · Saved only in this browser tab.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          id="guest-first-name"
          type="text"
          maxLength={40}
          autoComplete="given-name"
          value={draft}
          onChange={onChange}
          aria-describedby="guest-first-name-hint guest-first-name-msg"
          aria-invalid={err ? "true" : "false"}
          placeholder="First name"
          className="h-10 w-full sm:w-64 rounded-md border border-[#d1d5db] bg-white px-3 text-[15px] text-[#111827] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007a9e]/40"
          data-testid="guest-name-input"
        />
        {name && (
          <button type="button" onClick={onClear} className="text-[13px] font-semibold text-[#00698a] hover:underline" data-testid="guest-name-clear">
            Not you? Clear name
          </button>
        )}
      </div>
      <p id="guest-first-name-msg" aria-live="polite" className={`mt-2 text-[13px] ${err ? "text-[#991b1b]" : "text-[#4b5563]"}`} data-testid="guest-name-msg">{msg}</p>
    </div>
  );
};

/* Hero greeting — rendered only when a name exists. */
export const GuestGreeting = ({ className = "" }) => {
  const { name, clear } = useGuestName();
  if (!name) return null;
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="guest-greeting">
      <span className="text-[15px] font-semibold text-[#00698a]">Welcome, {name}.</span>
      <button type="button" onClick={clear} className="text-[13px] font-semibold text-[#6b7280] hover:text-[#111827] hover:underline" data-testid="guest-greeting-clear">
        Not you? Clear name
      </button>
    </div>
  );
};
